import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '../utils/api'
import { ClipboardList, Plus, X, Trash2, MapPin, User, CalendarClock, ListChecks } from 'lucide-react'
import { AssetSearchSelect } from './TicketsPage'
import RichTextEditor from '../components/RichTextEditor'

interface WorkOrder {
  id: string
  workOrderNumber: number
  title: string
  customerName: string
  location: string | null
  status: string
  scheduledDate: string | null
  createdAt: string
  asset: { id: string; assetCode: string | null; name: string } | null
  signatures: { id: string; signedAt: string }[]
  _count: { items: number }
}

const STATUS_LABEL: Record<string, string> = {
  DRAFT: 'ร่าง', IN_PROGRESS: 'กำลังดำเนินการ', COMPLETED: 'เสร็จงาน', SIGNED: 'ลงนามแล้ว', CANCELLED: 'ยกเลิก',
}
const STATUS_COLOR: Record<string, string> = {
  DRAFT:        'bg-gray-50 text-gray-600 border-gray-200',
  IN_PROGRESS:  'bg-blue-50 text-blue-700 border-blue-200',
  COMPLETED:    'bg-amber-50 text-amber-700 border-amber-200',
  SIGNED:       'bg-green-50 text-green-700 border-green-200',
  CANCELLED:    'bg-red-50 text-red-600 border-red-200',
}

interface NewForm {
  title: string; customerName: string; location: string; assetId: string
  description: string; scheduledDate: string; items: string[]
}
const emptyForm: NewForm = { title: '', customerName: '', location: '', assetId: '', description: '', scheduledDate: '', items: [''] }

function CreateModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [form, setForm] = useState<NewForm>(emptyForm)
  const [error, setError] = useState<string | null>(null)

  const createMut = useMutation({
    mutationFn: () => api.post('/workorders', {
      title: form.title, customerName: form.customerName, location: form.location || undefined,
      assetId: form.assetId || undefined, description: (form.description.replace(/<[^>]*>/g, '').trim() || /<(img|table)/.test(form.description)) ? form.description : undefined,
      scheduledDate: form.scheduledDate || undefined,
      items: form.items.map(i => i.trim()).filter(Boolean),
    }),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ['workorders'] }); navigate(`/workorders/${r.data.id}`) },
    onError: (e: any) => setError(e?.response?.data?.message || 'สร้างใบงานไม่สำเร็จ'),
  })

  const updateItem = (idx: number, value: string) => setForm(f => ({ ...f, items: f.items.map((it, i) => i === idx ? value : it) }))
  const addItem = () => setForm(f => ({ ...f, items: [...f.items, ''] }))
  const removeItem = (idx: number) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }))

  const ic = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 flex-shrink-0">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2"><ClipboardList size={16}/> สร้างใบงานใหม่</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X size={16}/></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {error && <div className="bg-red-50 text-red-600 text-xs px-3 py-2 rounded-lg">{error}</div>}

          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">หัวข้องาน *</label>
            <input className={ic} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="เช่น ซ่อมเครื่องพิมพ์ / ติดตั้งระบบเครือข่าย"/>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">ลูกค้า / หน่วยงาน *</label>
            <input className={ic} value={form.customerName} onChange={e => setForm(f => ({ ...f, customerName: e.target.value }))}/>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">สถานที่</label>
            <input className={ic} value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))}/>
          </div>
          <AssetSearchSelect label="ผูกกับ Asset (ถ้ามี)" value={form.assetId} onChange={id => setForm(f => ({ ...f, assetId: id }))}/>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">วันที่นัดหมาย</label>
            <input type="date" className={ic} value={form.scheduledDate} onChange={e => setForm(f => ({ ...f, scheduledDate: e.target.value }))}/>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">รายละเอียดงาน</label>
            <RichTextEditor
              content={form.description}
              onChange={html => setForm(f => ({ ...f, description: html }))}
              placeholder="อธิบายรายละเอียดงาน... (แทรกรูปภาพหรือตารางได้)"
              uploadEndpoint="/tickets/upload-image"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">รายการงาน (Checklist)</label>
            <div className="space-y-2">
              {form.items.map((item, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input className={ic} value={item} onChange={e => updateItem(idx, e.target.value)} placeholder={`รายการที่ ${idx + 1}`}/>
                  {form.items.length > 1 && (
                    <button onClick={() => removeItem(idx)} className="p-1.5 text-gray-400 hover:text-red-500 flex-shrink-0"><Trash2 size={15}/></button>
                  )}
                </div>
              ))}
            </div>
            <button onClick={addItem} className="mt-2 text-xs text-blue-600 font-medium flex items-center gap-1 hover:text-blue-700">
              <Plus size={13}/> เพิ่มรายการ
            </button>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 p-5 border-t border-gray-100 flex-shrink-0">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-xl">ยกเลิก</button>
          <button
            disabled={!form.title.trim() || !form.customerName.trim() || createMut.isPending}
            onClick={() => createMut.mutate()}
            className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
            {createMut.isPending ? 'กำลังบันทึก...' : 'สร้างใบงาน'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default function WorkOrdersPage({ canManageAssets }: { canManageAssets: boolean }) {
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [showCreate, setShowCreate] = useState(false)

  const { data: workOrders = [], isLoading } = useQuery<WorkOrder[]>({
    queryKey: ['workorders', statusFilter],
    queryFn: () => api.get('/workorders', { params: statusFilter ? { status: statusFilter } : {} }).then(r => r.data),
  })

  const fmtDate = (d: string | null) => d ? new Date(d).toLocaleDateString('th-TH', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2"><ClipboardList size={20}/> ใบงาน (Work Order)</h1>
        {canManageAssets && (
          <button onClick={() => setShowCreate(true)} className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700">
            <Plus size={16}/> สร้างใบงาน
          </button>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={() => setStatusFilter('')} className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${!statusFilter ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200'}`}>ทั้งหมด</button>
        {Object.entries(STATUS_LABEL).map(([v, label]) => (
          <button key={v} onClick={() => setStatusFilter(v)} className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${statusFilter === v ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200'}`}>{label}</button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm divide-y divide-gray-50">
        {isLoading && <div className="p-8 text-center text-gray-400 text-sm">กำลังโหลด...</div>}
        {!isLoading && workOrders.length === 0 && <div className="p-8 text-center text-gray-400 text-sm">ยังไม่มีใบงาน</div>}
        {workOrders.map(wo => (
          <Link key={wo.id} to={`/workorders/${wo.id}`} className="flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs text-gray-400">#{wo.workOrderNumber}</span>
                <span className="font-medium text-gray-900 truncate">{wo.title}</span>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full border flex-shrink-0 ${STATUS_COLOR[wo.status]}`}>{STATUS_LABEL[wo.status] || wo.status}</span>
              </div>
              <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 flex-wrap">
                <span className="flex items-center gap-1"><User size={11}/> {wo.customerName}</span>
                {wo.location && <span className="flex items-center gap-1"><MapPin size={11}/> {wo.location}</span>}
                <span className="flex items-center gap-1"><CalendarClock size={11}/> {fmtDate(wo.scheduledDate)}</span>
                <span className="flex items-center gap-1"><ListChecks size={11}/> {wo._count?.items ?? 0} รายการ</span>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {showCreate && <CreateModal onClose={() => setShowCreate(false)}/>}
    </div>
  )
}
